/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import IconButton from '@mui/material/IconButton';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ArrowBack from '@mui/icons-material/ArrowBackIosNew';
import ArrowNext from '@mui/icons-material/ArrowForwardIos';
import Toolbar from '@mui/material/Toolbar';
import AppBar from '@mui/material/AppBar';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import heic2any from 'heic2any';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SxProps, Theme } from '@mui/material/styles';
import { PaperProps } from '@mui/material/Paper/Paper';
import TwBackdrop from '../../TwBackdrop/TwBackdrop';
import NumberUtils from '../../../utils/numberUtils';
import { AttachmentBasicProps } from '../../../redux/api/orders/orders.model';
import { SearchDocumentAttachment } from '../../../redux/api/documentAttachments/documentAttachment.model';

// styles
import './TwGlobalDocumentViewer.scss';
import FileUtils from '../../../utils/fileUtils';

export enum TwDocumentType {
  pdf = 1,
  image = 2
}

export interface TwGlobalDocumentViewerProps {
  selectedFileId: string;
  open: boolean;
  formHasChanges?: boolean;
  files: SearchDocumentAttachment[] | AttachmentBasicProps[];
  setOpenWarning?: (open: boolean) => void;
  setOpenNextWarning?: (open: boolean, fileId: string) => void;
  onClose: (order_id: string | null) => void;
  // eslint-disable-next-line @typescript-eslint/ban-types
  PaperProps?: Partial<PaperProps<'div', {}>>;
  className?: string;
  sx?: SxProps;
}

export const getFileName = (file: SearchDocumentAttachment | AttachmentBasicProps): string => {
  if (!file) return '';

  return file.name;
};

const TwGlobalDocumentViewer: React.FC<TwGlobalDocumentViewerProps> = ({
  open,
  formHasChanges,
  files,
  selectedFileId,
  setOpenWarning,
  setOpenNextWarning,
  onClose,
  PaperProps,
  className,
  sx
}): JSX.Element => {
  const counter = useRef(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [fileSrc, setFileSrc] = useState<string | null>(null);
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(-1);
  const [pdfNumPages, setPdfNumPages] = useState<any>(null);
  const [pagesElements, setPagesElements] = useState<JSX.Element[]>([]);
  const matchesMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const matchesDesktopLarge = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));

  const dialogClasses = className
    ? `${className} tw-doc-viewer__dialog tid-document-viewer`
    : ` tw-doc-viewer__dialog tid-document-viewer`;

  useEffect(() => {
    if (!files && !selectedFileId) return;

    const fileIndex = files.findIndex((file) => file.id === selectedFileId);
    if (fileIndex < 0) return;

    setCurrentFileIndex(fileIndex);
  }, [selectedFileId, files]);

  useEffect(() => {
    if (!files || files.length === 0 || !open) setLoading(true);

    if (files && files.length !== 0 && open && currentFileIndex > -1) {
      setFileSrc(null);

      setLoading(true);
      (async (): Promise<void> => {
        const src =
          files[currentFileIndex] &&
          (await getFileSrc(files[currentFileIndex].s3_signed_url || ''));
        setFileSrc(src);
        setLoading(false);
      })();
    }
  }, [files, currentFileIndex, open]);

  useEffect(() => {
    const pages: JSX.Element[] = [];
    if (pdfNumPages) {
      Array.from(Array(pdfNumPages).keys()).map((item, i) => {
        pages.push(
          <Page
            scale={matchesMobile ? 0.45 : matchesDesktopLarge ? 1.7 : 1}
            className="tw-doc-viewer__viewerPdfPage"
            key={i}
            pageNumber={i + 1}
            renderAnnotationLayer={false}
            renderTextLayer
            onClick={(e: any): void => e.stopPropagation()}
          />
        );
      });
      setPagesElements(pages);
    }
  }, [pdfNumPages]);

  const handleClose = (e: any, reason: any): void => {
    if (reason && reason == 'backdropClick') return;

    setFileSrc(null);
    setPdfNumPages(null);
    setLoading(true);
    onClose(null);
  };

  const handleOpenWarning = (): void => {
    if (setOpenWarning) {
      setOpenWarning(true);
      return;
    }

    handleClose(null, null);
  };

  const handleNextDoc = (e: any): void => {
    e.stopPropagation();
    if (!files) return;

    setOpenNextWarning &&
      setOpenNextWarning(formHasChanges || false, files[currentFileIndex + 1].id);
  };

  const handlePrevDoc = (e: any): void => {
    e.stopPropagation();
    if (!files) return;

    setOpenNextWarning &&
      setOpenNextWarning(formHasChanges || false, files[currentFileIndex - 1].id);
  };

  const getFileSrc = async (awsUrl: string): Promise<string> => {
    try {
      let fileBlob: Blob | null = null;

      try {
        // Fetch the file directly using the awsUrl
        const response = await fetch(awsUrl);
        if (response.ok) {
          fileBlob = await response.blob();
        } else {
          console.error('Failed to fetch file from AWS:', response);
        }
      } catch (error) {
        console.error('Error fetching file from AWS URL:', error);
      }

      if (!fileBlob) {
        // shouldn't reach here
        throw new Error('Failed to retrieve the file.');
      }

      // Inspect the MIME type of the file
      const mimeType = fileBlob.type;

      if (mimeType === 'application/pdf') {
        // Handle PDF files: directly create an object URL
        return URL.createObjectURL(fileBlob);
      } else if (mimeType === 'image/heif' || mimeType === 'image/heic') {
        // Handle HEIF/HEIC files
        const heicBlob = await heic2any({ blob: fileBlob, toType: 'image/jpeg' });
        return URL.createObjectURL(heicBlob as Blob);
      }

      // Fallback: Return the object URL for other formats
      return URL.createObjectURL(fileBlob);
    } catch (error) {
      return '';
    } finally {
      setLoading(false);
    }
  };

  const getPreview = (file?: SearchDocumentAttachment | AttachmentBasicProps): JSX.Element => {
    counter.current += 1;
    let docIsPhoto = false;
    let docIsPdf = false;

    if (file) {
      if ('file_type' in file) {
        docIsPhoto = file?.file_type.includes('image');
        docIsPdf = file?.file_type.includes('pdf') || file?.file_type.includes('octet-stream');
      }
    }

    if (fileSrc && docIsPhoto) {
      return (
        <img
          src={fileSrc}
          width="400px"
          className={`tw-doc-viewer__viewerPhoto tid-document-viewer__photo`}
          onClick={(e: any): void => e.stopPropagation()}
        />
      );
    } else if (fileSrc && docIsPdf) {
      return (
        <Document
          file={fileSrc}
          onLoadSuccess={onPDFLoadSuccess}
          className={`${
            pdfNumPages <= 1 && matchesMobile
              ? 'tw-doc-viewer__justify-center'
              : 'tw-doc-viewer__justify-start'
          } tw-doc-viewer__viewerDoc tid-document-viewer__pdf`}
        >
          {pagesElements}
        </Document>
      );
    }

    if (!loading && !fileSrc) {
      return <span className="tw-doc-viewer__error">Could not load preview</span>;
    }

    return <></>;
  };

  const onPDFLoadSuccess = (params: any): void => {
    setPdfNumPages(params._pdfInfo.numPages);
    setLoading(false);
  };

  const handleDownload = async (
    fileName: string,
    url: string | null,
    downloadUrl: string | null
  ): Promise<void> => {
    if (!url) return;
    if (downloadUrl) {
      FileUtils.fetchAndDownloadFileLinkClick(downloadUrl);
    } else {
      try {
        // Fetch the file content
        const response = await fetch(url);
        const blob = await response.blob();

        // Create a blob URL
        const blobUrl = window.URL.createObjectURL(blob);

        // Create and trigger download
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      } catch (error) {
        console.error('Error downloading file:', error);
      }
    }
  };

  return (
    <Dialog
      className={dialogClasses}
      open={open}
      onClose={handleOpenWarning}
      fullScreen
      fullWidth={true}
      hideBackdrop
      disableEnforceFocus
      PaperProps={PaperProps}
      sx={sx}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <DialogTitle className={`tw-doc-viewer__title tid-document-viewer__title`}>
            <InsertDriveFileOutlinedIcon className="tw-doc-viewer__titleIcon" />
            <div className="tw-doc-viewer__fileName">{getFileName(files[currentFileIndex])}</div>
            {files && files[currentFileIndex] && (
              <span className="tw-doc-viewer__fileSize">
                {NumberUtils.getFileSize(files[currentFileIndex].size)}
              </span>
            )}
          </DialogTitle>
          {files && (
            <span className="tw-doc-viewer__viewerFileCount">
              File {currentFileIndex + 1} of {files?.length}
            </span>
          )}
          <div className={`tw-doc-viewer__actions tid-document-viewer__actions`}>
            <IconButton
              className="tid-document-viewer__download-btn"
              edge="start"
              onClick={(): void => {
                files &&
                  files[currentFileIndex] &&
                  handleDownload(
                    files[currentFileIndex].name,
                    files[currentFileIndex].s3_signed_url || '',
                    files[currentFileIndex].s3_signed_download_url || null
                  );
              }}
              aria-label="download"
            >
              <DownloadIcon className="tw-doc-viewer__actionItem" />
            </IconButton>

            <IconButton
              edge="start"
              onClick={(): void => {
                handleOpenWarning();
              }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </Toolbar>
      </AppBar>
      <DialogContent
        className={`tw-doc-viewer__content tid-document-viewer__content`}
        onClick={(e: any): void => {
          e.stopPropagation();
        }}
      >
        {files && files.length > 0 && currentFileIndex > 0 && (
          <IconButton
            edge="start"
            onClick={handlePrevDoc}
            aria-label="previous"
            sx={{
              left: '20px'
            }}
            className="tw-doc-viewer__viewerNav"
          >
            <ArrowBack />
          </IconButton>
        )}
        {getPreview(files[currentFileIndex])}
        <TwBackdrop isOpen={loading} className="tid-document-viewer__loader" position="absolute" />

        {files && files.length > 0 && currentFileIndex < files.length - 1 && (
          <IconButton
            edge="start"
            onClick={handleNextDoc}
            aria-label="next"
            sx={{ right: '10px' }}
            className="tw-doc-viewer__viewerNav"
          >
            <ArrowNext />
          </IconButton>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TwGlobalDocumentViewer;
